<template>
  <div class="return-transfer-access-page">
    <h2 class="page-title mb-4">Atur Akses Transfer Balik</h2>

    <div class="setting-card">
      <!-- Card Header -->
      <div class="card-header-section">
        <h3 class="card-main-title">Daftar Nama POS</h3>
        <p class="card-description">
          Pilih nama POS yang dapat melakukan Pengembalian QR ke Dealer. POS yang tidak dicentang tidak akan melihat tombol Pengembalian QR.
        </p>
      </div>

      <!-- Filter & Information Row -->
      <div class="filter-info-row d-flex justify-content-between align-items-center mb-4">
        <div class="info-user-allowed">
          <span class="count-highlight">{{ checkedCount }}</span>
          <span class="text-muted-desc"> dari {{ totalUserCount }} user POS diizinkan</span>
        </div>

        <div class="search-input-wrapper">
          <input
            type="text"
            v-model="searchQuery"
            class="form-control search-pos-input"
            :class="{ 'has-value': searchQuery.trim().length > 0 }"
            placeholder="Cari Kode atau Nama POS"
          />
          <i class="fas fa-search search-icon"></i>
        </div>
      </div>

      <!-- Table Section -->
      <div class="table-responsive">
        <table class="table custom-access-table mb-0">
          <thead>
            <tr>
              <th class="th-checkbox">
                <span
                  class="access-tooltip"
                  :data-tooltip="isUserSE ? noAccessTooltip : null"
                >
                  <input
                    type="checkbox"
                    class="custom-table-checkbox"
                    :checked="isAllSelected"
                    :indeterminate.prop="isPartialSelected"
                    :disabled="isUserSE"
                    @change="handleToggleAll($event.target.checked)"
                  />
                </span>
              </th>
              <th>Kode Pos</th>
              <th>Nama Pos</th>
              <th>Dealer</th>
              <th class="th-akses">Akses</th>
            </tr>
          </thead>
          <tbody v-if="rows.length > 0">
            <tr
              v-for="item in rows"
              :key="item.userId"
            >
            <td class="td-checkbox">
              <span
                class="access-tooltip"
                :data-tooltip="isUserSE ? noAccessTooltip : null"
              >
                <input
                  type="checkbox"
                  class="custom-table-checkbox"
                  :checked="allowedIdSet.has(item.userId)"
                  :disabled="isUserSE"
                  @change="handleToggleAccess(item.userId, $event.target.checked)"
                />
              </span>
            </td>
              <td class="cell-kode-pos">{{ item.posCode }}</td>
              <td class="cell-nama-pos">{{ item.posName }}</td>
              <td class="cell-dealer">{{ item.dealerName }}</td>
              <td class="cell-akses">
                <span v-if="allowedIdSet.has(item.userId)" class="text-akses-allowed">Diizinkan</span>
                <span v-else class="text-akses-empty">&mdash;</span>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="5" class="td-empty-search text-center">
                <div class="empty-search-state">
                  <div class="empty-search-icon-wrapper">
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="28" cy="28" r="28" fill="#F4F5F7"/>
                      <circle cx="26" cy="26" r="7.5" stroke="#9CA3AF" stroke-width="2.2"/>
                      <path d="M31.5 31.5L37.5 37.5" stroke="#9CA3AF" stroke-width="2.2" stroke-linecap="round"/>
                    </svg>
                  </div>
                  <h5 class="empty-search-title">User POS tidak ditemukan</h5>
                  <p class="empty-search-desc mb-0" v-if="searchQuery.trim()">
                    Tidak ada user POS yang cocok dengan kata kunci &ldquo;{{ searchQuery.trim() }}&rdquo;. Coba kata kunci lain
                  </p>
                  <p class="empty-search-desc mb-0" v-else>
                    Belum ada data user POS.
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Section -->
      <div class="pagination-section d-flex justify-content-between align-items-center mt-3 pt-2" v-if="rows.length > 0">
        <span class="pagination-info">
          Showing {{ paginationStart }}-{{ paginationEnd }} of {{ total }} Items
        </span>

        <div class="pagination-controls d-flex align-items-center">
          <button
            type="button"
            class="btn-nav-page"
            :disabled="currentPage <= 1"
            @click="goToPage(1)"
            aria-label="First Page"
          >
            <i class="fa fa-angle-double-left"></i>
          </button>
          <button
            type="button"
            class="btn-nav-page"
            :disabled="currentPage <= 1"
            @click="goToPage(currentPage - 1)"
            aria-label="Previous Page"
          >
            <i class="fa fa-angle-left"></i>
          </button>

          <button
            v-for="page in totalPages"
            :key="page"
            type="button"
            class="btn-page-number"
            :class="{ 'active': page === currentPage }"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>

          <button
            type="button"
            class="btn-nav-page"
            :disabled="currentPage >= totalPages"
            @click="goToPage(currentPage + 1)"
            aria-label="Next Page"
          >
            <i class="fa fa-angle-right"></i>
          </button>
          <button
            type="button"
            class="btn-nav-page"
            :disabled="currentPage >= totalPages"
            @click="goToPage(totalPages)"
            aria-label="Last Page"
          >
            <i class="fa fa-angle-double-right"></i>
          </button>
        </div>
      </div>

      <!-- Card Bottom Actions -->
      <div v-if="!isUserSE" class="card-actions-section d-flex justify-content-end align-items-center">
        <button
          type="button"
          class="btn btn-action-batal mr-3"
          @click="handleCancel"
        >
          Batal
        </button>
        <button
          type="button"
          class="btn btn-action-simpan"
          :class="{ 'btn-action-simpan--active': isChanged && !isSaving }"
          :disabled="!isChanged || isSaving"
          @click="handleSave"
        >
          <span v-if="isSaving">Menyimpan...</span>
          <span v-else>Simpan</span>
        </button>
      </div>
    </div>

    <!-- Success Toast Notification (Top Right) -->
    <transition name="toast-slide">
      <div v-if="showSuccessToast" class="top-right-toast">
        <div class="toast-icon-wrapper">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="12" fill="#22C55E"/>
            <path d="M7 12.5L10.2 15.7L17 8.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="toast-content-wrapper">
          <h6 class="toast-title mb-1">Berhasil disimpan</h6>
          <p class="toast-desc mb-0">
            Akses transfer balik untuk {{ savedCount }} user POS berhasil diperbarui.
          </p>
        </div>
        <button
          type="button"
          class="btn toast-close-btn"
          @click="showSuccessToast = false"
          aria-label="Tutup"
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L13 13M1 13L13 1" stroke="#9CA3AF" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </transition>

    <!-- Unsaved Changes Confirmation Modal -->
    <transition name="modal">
      <div v-if="showUnsavedModal" class="confirm-modal-mask">
        <div class="confirm-modal-wrapper" @click="cancelLeave">
          <div class="confirm-modal-container" @click.stop>
            <!-- Header -->
            <div class="confirm-header d-flex justify-content-between align-items-center">
              <h6 class="confirm-title mb-0">Perubahan Belum Disimpan</h6>
              <button
                type="button"
                class="btn btn-close-confirm"
                @click="cancelLeave"
                aria-label="Tutup"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L13 13M1 13L13 1" stroke="#111827" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>

            <!-- Body Content -->
            <div class="confirm-body text-center">
              <div class="confirm-icon-wrapper">
                <svg
                  width="108"
                  height="98"
                  viewBox="0 0 124 112"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M53.493 2.18918C61.9236 -2.48607 72.6478 0.58571 77.359 8.94096L122.425 87.1547C123.417 89.4893 123.851 91.3877 123.975 93.3597C124.223 97.9674 122.611 102.446 119.45 105.893C116.288 109.327 112.011 111.354 107.362 111.6H16.6092C14.6875 111.483 12.7659 111.047 10.9682 110.371C1.97967 106.747 -2.3596 96.5482 1.29778 87.7077L46.6741 8.88567C48.2239 6.11492 50.5795 3.72507 53.493 2.18918ZM61.9856 76.0902C59.0101 76.0902 56.5305 78.5476 56.5305 81.5027C56.5305 84.4454 59.0101 86.909 61.9856 86.909C64.9611 86.909 67.3786 84.4454 67.3786 81.4351C67.3786 78.4923 64.9611 76.0902 61.9856 76.0902ZM61.9856 37.7604C59.0101 37.7604 56.5305 40.1503 56.5305 43.1054V60.4855C56.5305 63.4344 59.0101 65.898 61.9856 65.898C64.9611 65.898 67.3786 63.4344 67.3786 60.4855V43.1054C67.3786 40.1503 64.9611 37.7604 61.9856 37.7604Z"
                    fill="#F36F21"
                  />
                </svg>
              </div>

              <div class="confirm-text">
                <p class="unsaved-text-msg mb-2">
                  Perubahan pada halaman ini belum disimpan dan akan hilang jika Anda keluar.
                </p>
                <p class="unsaved-text-confirm mb-0">
                  Yakin ingin keluar tanpa menyimpan?
                </p>
              </div>
            </div>

            <!-- Actions -->
            <div class="confirm-actions d-flex justify-content-center align-items-center">
              <button
                type="button"
                class="btn btn-confirm-batal"
                @click="cancelLeave"
              >
                Batal
              </button>
              <button
                type="button"
                class="btn btn-confirm-keluar"
                @click="confirmLeave"
              >
                Ya, Keluar
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  fetchAllReturnAccessIds,
  fetchReturnAccessPage,
} from "@/services/return-access";

export default {
  name: "ReturnTransferAccess",
  metaInfo: {
    title: "Atur Akses Transfer Balik",
  },
  data() {
    return {
      searchQuery: "",
      currentPage: 1,
      pageSize: 6,
      isSaving: false,
      showSuccessToast: false,
      toastTimeout: null,
      savedCount: 0,
      showUnsavedModal: false,
      // { kind: "route", next } | { kind: "discard" }
      pendingLeave: null,
      forceLeave: false,
      rows: [],
      total: 0,
      lastPage: 1,
      totalPosUsers: 0,
      allowedIds: [],
      initialAllowedIds: [],
      hasLoadedAccess: false,
      latestRequestId: 0,
      // Every user ID matching the current search, across all pages; null
      // while loading or after a failed load.
      matchingIds: null,
      latestIdsRequestId: 0,
      noAccessTooltip: "Anda tidak memiliki izin untuk mengubah hak akses.",
    };
  },
  computed: {
    allowedIdSet() {
      return new Set(this.allowedIds);
    },
    checkedCount() {
      return this.allowedIds.length;
    },
    totalUserCount() {
      return this.totalPosUsers;
    },
    totalPages() {
      return Math.max(1, this.lastPage);
    },
    paginationStart() {
      if (this.total === 0) return 0;
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.pageSize, this.total);
    },
    // Falls back to the current page until the full ID list has loaded.
    selectAllIds() {
      return this.matchingIds || this.rows.map((x) => x.userId);
    },
    isAllSelected() {
      if (this.selectAllIds.length === 0) return false;
      return this.selectAllIds.every((id) => this.allowedIdSet.has(id));
    },
    isPartialSelected() {
      const count = this.selectAllIds.filter((id) => this.allowedIdSet.has(id)).length;
      return count > 0 && count < this.selectAllIds.length;
    },
    isChanged() {
      if (this.allowedIds.length !== this.initialAllowedIds.length) return true;
      const initialSet = new Set(this.initialAllowedIds);
      return this.allowedIds.some((id) => !initialSet.has(id));
    },
    isUserSE() {
      return this.$store.state.userData.userData.role === 'USER SE';
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
      this.loadPage();
      this.loadMatchingIds();
    },
  },
  beforeMount() {
    const el = document.getElementById("content");
    if (el) el.style.background = "#F8F8F8";
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeDestroy() {
    const el = document.getElementById("content");
    if (el) el.style.background = "";
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
  },
  mounted() {
    this.loadPage();
    this.loadMatchingIds();
  },
  beforeRouteLeave(to, from, next) {
    if (this.canLeaveRoute(to, next)) {
      next();
    }
  },
  methods: {
    canLeaveRoute(to, nextCallback) {
      return this.requestLeave({ kind: "route", next: nextCallback });
    },
    requestLeave(intent) {
      if (this.isChanged && !this.forceLeave) {
        this.pendingLeave = intent;
        this.showUnsavedModal = true;
        return false;
      }
      return true;
    },
    cancelLeave() {
      this.showUnsavedModal = false;
      this.pendingLeave = null;
    },
    confirmLeave() {
      const intent = this.pendingLeave;
      this.showUnsavedModal = false;
      this.pendingLeave = null;
      if (intent) this.applyLeave(intent);
    },
    applyLeave(intent) {
      if (intent.kind === "route") {
        this.forceLeave = true;
        intent.next();
        return;
      }
      this.allowedIds = this.initialAllowedIds.slice();
      this.leavePage();
    },
    leavePage() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push({ name: "Welcome" }).catch(() => {});
      }
    },
    handleBeforeUnload(e) {
      if (this.isChanged && !this.forceLeave) {
        e.preventDefault();
        e.returnValue = "";
      }
    },
    triggerSuccessToast() {
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout);
      }
      this.showSuccessToast = true;
      this.toastTimeout = setTimeout(() => {
        this.showSuccessToast = false;
      }, 4000);
    },
    async loadPage() {
      const requestId = ++this.latestRequestId;
      try {
        const page = await fetchReturnAccessPage(this.$http, {
          page: this.currentPage,
          limit: this.pageSize,
          search: this.searchQuery.trim(),
        });
        if (requestId !== this.latestRequestId) return;

        this.rows = page.rows;
        this.total = page.total;
        this.lastPage = page.lastPage;
        this.totalPosUsers = page.totalPosUsers;
        if (!this.hasLoadedAccess) {
          this.allowedIds = page.allowedUserIds.slice();
          this.initialAllowedIds = page.allowedUserIds.slice();
          this.hasLoadedAccess = true;
        }
      } catch (err) {
        if (requestId !== this.latestRequestId) return;
        console.error("Gagal memuat return-access:", err);
        this.$swal.fire({
          title: "Gagal Memuat Data",
          text: (err && err.message) || "Terjadi kesalahan saat memuat daftar user POS",
          icon: "error",
          confirmButtonColor: "#F36F21",
        });
      }
    },
    async loadMatchingIds() {
      const requestId = ++this.latestIdsRequestId;
      this.matchingIds = null;
      try {
        const ids = await fetchAllReturnAccessIds(this.$http, {
          search: this.searchQuery.trim(),
        });
        if (requestId !== this.latestIdsRequestId) return;
        this.matchingIds = ids;
      } catch (err) {
        if (requestId !== this.latestIdsRequestId) return;
        console.error("Gagal memuat semua user POS:", err);
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.loadPage();
      }
    },
    handleToggleAccess(userId, checked) {
      if (this.isUserSE) return;
      const others = this.allowedIds.filter((id) => id !== userId);
      this.allowedIds = checked ? others.concat(userId) : others;
    },
    handleToggleAll(checked) {
      if (this.isUserSE) return;
      const targetIds = new Set(this.selectAllIds);
      const others = this.allowedIds.filter((id) => !targetIds.has(id));
      this.allowedIds = checked ? others.concat(Array.from(targetIds)) : others;
    },
    handleCancel() {
      const intent = { kind: "discard" };
      if (this.requestLeave(intent)) this.applyLeave(intent);
    },
    async handleSave() {
      if (this.isUserSE) return;
      this.isSaving = true;
      try {
        await this.$http.put("/api/qrs/return-access", {
          user_ids: this.allowedIds.slice(),
        });

        this.initialAllowedIds = this.allowedIds.slice();
        this.savedCount = this.allowedIds.length;
        this.triggerSuccessToast();
      } catch (err) {
        this.$swal.fire({
          title: "Gagal Menyimpan",
          text: (err.response && err.response.data && err.response.data.message) || "Terjadi kesalahan saat menyimpan akses transfer balik",
          icon: "error",
          confirmButtonColor: "#F36F21",
        });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.return-transfer-access-page {
  font-family: inherit;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #2F2F2F;
  line-height: 24px;
}

.setting-card {
  background: #FFFFFF;
  border-radius: 8px;
  border: 1px solid #ECECEC;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  padding: 28px 32px 24px 32px;
}

.card-header-section {
  border-bottom: 1px solid #F3F4F6;
  padding-bottom: 18px;
  margin-bottom: 24px;
}

.card-main-title {
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  color: #2F2F2F;
  margin-bottom: 4px;
}

.card-description {
  margin: 0;
  font-size: 12px;
  line-height: normal;
  color: #707070;
}

/* Filter & Info */
.info-user-allowed {
  font-size: 13px;
}

.count-highlight {
  color: #F36F21;
  font-weight: 600;
  font-size: 14px;
}

.text-muted-desc {
  color: #6B7280;
}

.search-input-wrapper {
  position: relative;
  width: 260px;
}

.search-pos-input {
  height: 36px;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  font-size: 13px;
  color: #2F2F2F;
  padding: 6px 36px 6px 12px;
  box-shadow: none;
  background-color: #FFFFFF;
  transition: border-color 0.2s;
}

.search-pos-input:focus,
.search-pos-input.has-value {
  border-color: #F36F21;
  box-shadow: none;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: #2F2F2F;
  pointer-events: none;
}

/* Table */
.custom-access-table {
  border-collapse: collapse;
}

.custom-access-table thead th {
  font-size: 12px;
  font-weight: 600;
  color: #2F2F2F;
  border-top: none;
  border-bottom: 1px solid #F0F0F0;
  padding: 12px 16px;
  vertical-align: middle;
  white-space: nowrap;
}

.th-checkbox,
.td-checkbox {
  width: 44px;
  padding-left: 16px !important;
  padding-right: 8px !important;
  text-align: center;
}

.custom-table-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #F36F21;
  border-radius: 3px;
  vertical-align: middle;
}

.custom-access-table tbody tr {
  border-bottom: 1px solid #F6F7F8;
  transition: background-color 0.15s;
}

.custom-access-table tbody tr:hover {
  background-color: #FAFAFA;
}


.custom-access-table tbody td {
  font-size: 13px;
  font-weight: 400;
  color: #2F2F2F;
  padding: 14px 16px;
  vertical-align: middle;
  border-top: none;
}

.th-akses {
  width: 140px;
}

.cell-kode-pos {
  font-weight: 500;
  white-space: nowrap;
}

.cell-nama-pos {
  color: #2F2F2F;
}

.cell-dealer {
  color: #4B5563;
}

.cell-akses {
  white-space: nowrap;
}

.text-akses-allowed {
  font-size: 14px;
  font-weight: 600;
  color: #22C55E;
  line-height: 1.4;
  display: inline-block;
}

.text-akses-empty {
  color: #9CA3AF;
  font-size: 16px;
  line-height: 1.4;
  display: inline-block;
}

/* Empty Search State */
.td-empty-search {
  padding: 64px 20px !important;
  border-top: none !important;
  border-bottom: none !important;
}

.empty-search-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-search-icon-wrapper {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-search-icon-wrapper svg {
  display: block;
}

.empty-search-title {
  font-size: 15px;
  font-weight: 600;
  color: #1F2937;
  margin-bottom: 6px;
}

.empty-search-desc {
  font-size: 13px;
  color: #6B7280;
  max-width: 440px;
  line-height: 1.5;
  text-align: center;
}

/* Pagination */
.pagination-info {
  font-size: 12px;
  color: #9CA3AF;
  font-weight: 400;
}

.pagination-controls {
  gap: 2px;
}

.btn-nav-page {
  background: transparent;
  border: none;
  color: #9CA3AF;
  font-size: 12px;
  padding: 4px 6px;
  margin: 0 1px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s;
  line-height: 1;
}

.btn-nav-page:hover:not(:disabled) {
  color: #374151;
}

.btn-nav-page:disabled {
  color: #D1D5DB;
  cursor: not-allowed;
}

.btn-page-number {
  background: transparent;
  border: none;
  font-size: 12px;
  font-weight: 400;
  color: #6B7280;
  padding: 3px 8px;
  min-width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  line-height: 1;
}

.btn-page-number:hover {
  color: #F36F21;
}

.btn-page-number.active {
  color: #111827;
  font-weight: 600;
  cursor: default;
}

/* Actions Section */
.card-actions-section {
  margin-top: 40px;
  padding-top: 16px;
}

.btn-action-batal {
  background: transparent;
  border: none;
  color: #F36F21;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  cursor: pointer;
  line-height: 1;
}

.btn-action-batal:hover {
  color: #D95D16;
}

.btn-action-simpan {
  height: 36px;
  min-width: 90px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  padding: 0 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #CBD5E1;
  color: #FFFFFF;
  cursor: not-allowed;
  transition: background-color 0.2s;
  line-height: 1;
}

.btn-action-simpan--active {
  background-color: #F36F21 !important;
  color: #FFFFFF !important;
  cursor: pointer !important;
}

.btn-action-simpan--active:hover {
  background-color: #D95D16 !important;
}

/* Top Right Toast */
.top-right-toast {
  position: fixed;
  top: 72px;
  right: 24px;
  z-index: 2000;
  width: 360px;
  height: 75px;
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-width: 1px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: flex-start;
  padding: 13px 16px;
  gap: 12px;
  box-sizing: border-box;
  transform: rotate(0deg);
  opacity: 1;
}

.toast-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.toast-icon-wrapper svg {
  display: block;
}

.toast-content-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.toast-title {
  font-size: 14px;
  font-weight: 600;
  color: #1F2937;
  line-height: 1.25;
}

.toast-desc {
  font-size: 13px;
  color: #6B7280;
  line-height: 1.35;
  white-space: normal;
}

.toast-close-btn {
  background: transparent;
  border: none;
  padding: 2px;
  color: #9CA3AF;
  font-size: 13px;
  cursor: pointer;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  box-shadow: none;
  flex-shrink: 0;
  margin-top: 1px;
  transition: opacity 0.15s;
}

.toast-close-btn:hover {
  opacity: 0.7;
}

.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-slide-enter {
  opacity: 0;
  transform: translateY(-20px) scale(0.96);
}

.toast-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.96);
}

/* Unsaved Changes Confirmation Modal Styles */
.confirm-modal-mask {
  position: fixed;
  z-index: 1060;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
  padding: 20px;
}

.confirm-modal-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.confirm-modal-container {
  width: 401px;
  max-width: 401px;
  height: 330px;
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  box-sizing: border-box;
}

.confirm-header {
  width: 100%;
}

.confirm-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.btn-close-confirm {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  box-shadow: none;
}

.btn-close-confirm:hover svg path {
  stroke: #4B5563;
}

.confirm-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto 0;
}

.confirm-icon-wrapper {
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
}

.confirm-icon-wrapper svg {
  display: block;
}

.confirm-text {
  max-width: 320px;
}

.unsaved-text-msg {
  font-size: 13.5px;
  color: #1F2937;
  line-height: 1.45;
  text-align: center;
  margin-bottom: 8px;
}

.unsaved-text-confirm {
  font-size: 13.5px;
  color: #1F2937;
  line-height: 1.45;
  text-align: center;
}

.confirm-actions {
  margin-top: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

.btn-confirm-batal {
  background: transparent;
  border: none;
  color: #F36F21;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  cursor: pointer;
  box-shadow: none;
  outline: none;
  line-height: 1;
}

.btn-confirm-batal:hover {
  color: #D95D16;
}

.btn-confirm-keluar {
  background-color: #F36F21;
  border: none;
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  padding: 8px 24px;
  min-width: 140px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  cursor: pointer;
  line-height: 1;
  transition: background-color 0.15s;
}

.btn-confirm-keluar:hover {
  background-color: #D95D16;
}


.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .confirm-modal-container,
.modal-leave-active .confirm-modal-container {
  -webkit-transform: scale(1.05);
  transform: scale(1.05);
}
.custom-table-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* CSS tooltip for the checkbox wrapper; always light, regardless of device theme */
.access-tooltip {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.access-tooltip[data-tooltip]::after {
  content: attr(data-tooltip);
  position: absolute;
  left: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  padding: 8px 12px;
  background-color: #FFFFFF;
  border: 1px solid #F3F3F3;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  line-height: 14px;
  font-weight: 400;
  color: #333333;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease;
}

.access-tooltip[data-tooltip]:hover::after {
  opacity: 1;
  visibility: visible;
}
</style>
